import {createHttpLock, useLockEffect} from '@raison/network-facade';
import {Decoder} from '@raison/prelude';
import refreshLineIcon from '@raison/static/icons/refresh-line.svg';
import {simpleStore} from '@raison/toolbox';
import {InlineSvg, RsnButton, RsnDialog} from '@raison/ui-kit';
import * as Sentry from '@sentry/react';
import React, {useState, useSyncExternalStore} from 'react';
import {SemVer} from '~/entities/versions';
import {appSessionStorage} from '~/shared/persistence';
import {usePolling} from './usePolling';

const CHECK_INTERVAL = 60_000;
const VERSION_URL = '/version.txt';

export const embeddedVersion = Decoder.run(SemVer.fromString, RELEASE_VERSION).orThrow();
const deployedVersion$ = simpleStore<SemVer | undefined>(undefined);

export function useDeployedVersion() {
    const deployed = useSyncExternalStore(deployedVersion$.subscribe, deployedVersion$.snapshot);
    const change = deployed && SemVer.diff(embeddedVersion, deployed);
    const isOutdated = change != null;
    return {deployed, isOutdated};
}

async function fetchDeployedVersion() {
    const version = await requestVersion();
    if (version) deployedVersion$.setState(version);
}

let requestsCount = 0;

async function requestVersion(): Promise<SemVer | null> {
    try {
        const cacheBust = `c=${requestsCount++}&d=${Date.now()}&r=${Math.random().toString(36).substring(2)}`;
        const response = await fetch(`${VERSION_URL}?${cacheBust}`, {
            headers: {
                'Cache-Control': 'no-store',
                'Rsn-Version': SemVer.format(embeddedVersion),
                'Rsn-User-Id': appSessionStorage.userId.get() ?? 'none',
            },
            cache: 'no-store',
            credentials: 'omit',
        });
        const contentType = response.headers.get('content-type');
        if (contentType && !contentType.startsWith('text/plain')) {
            throw new Error(`Unexpected mime type on version.txt "${contentType}"`);
        }
        const json = await response.text();
        const deployedVersion = Decoder.run(SemVer.fromString, json).orThrow();
        return deployedVersion;
    } catch (error) {
        const isOk = await fetch('_healthz')
            .then(({ok}) => ok)
            .catch(() => false);
        if (isOk) {
            const e = new Error('Version request failed', {cause: error});
            Sentry.captureException(e, {
                level: 'warning',
            });
            return null;
        } else {
            return null;
        }
    }
}

export function AppAutoUpdater() {
    const {isOutdated: hasToReload} = useDeployedVersion();
    usePolling({
        poll: fetchDeployedVersion,
        interval: CHECK_INTERVAL,
    });

    const [reloading, setReloadStarted] = useState(false);

    function onReload() {
        if (reloading) return;
        setReloadStarted(true);
        window.location.reload();
    }

    useLockEffect(AppAutoUpdater.httpLock, hasToReload);

    return (
        <RsnDialog.Controller onClose={onReload}>
            {hasToReload && (
                <RsnDialog.Window>
                    <RsnDialog.CenteredHeader text='Application updated' icon={{element: refreshIcon}} />
                    <RsnDialog.Body>
                        The app will restart, and the updates will be applied once you close this message.
                    </RsnDialog.Body>
                    <RsnDialog.Footer>
                        <RsnButton.Secondary onClick={onReload}>
                            {reloading ? 'Reloading...' : `I got it`}
                        </RsnButton.Secondary>
                    </RsnDialog.Footer>
                </RsnDialog.Window>
            )}
        </RsnDialog.Controller>
    );
}

const refreshIcon = (
    <div className={'flex size-16 items-center justify-center rounded-full bg-neutral-20'}>
        <InlineSvg src={refreshLineIcon} className={'size-8'} />
    </div>
);
AppAutoUpdater.httpLock = createHttpLock();
